/**
 * Budget chooser body — used in two places:
 *   - the legacy BudgetStep page (still routable at /campaign/new/budget)
 *   - the "Как определить бюджет?" popover on the Bloggers step
 *
 * Identical UI (presets + slider + forecast + tiers + benchmarks). Reads /
 * writes draft.budget via the shared draft store.
 */
import { useState } from "react";
import { Sparkles, TrendingUp, BarChart, CheckCircle2, Lock } from "lucide-react";
import { useDraftStore } from "@/shared/store/draft";
import { useBudgetConfig, useForecast, useCategories } from "@/shared/api/wb";
import { Card, InfoTip, Slider } from "@/shared/ui";
import { fmtNum, fmtRub, fmtRubFull } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";

export function BudgetPicker() {
  const { draft } = useDraftStore();
  const cfg = useBudgetConfig().data;
  const fc = useForecast(draft.budget || 70000).data;
  const cats = useCategories().data ?? [];
  const cat = cats.find((c) => c.id === draft.productCategory);
  const [tipOpen, setTipOpen] = useState(false);
  const setBudget = useDraftStore((s) => s.setBudget);

  return (
    <>
      <Card className="!p-4 mb-4 flex items-start gap-3.5">
        <div className="w-9 h-9 rounded-md bg-lime grid place-items-center text-[var(--fg-on-accent)] flex-shrink-0">
          <Sparkles size={16} />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-[13.5px] mb-1">Рекомендация по бюджету</div>
          <div className="text-[13px] text-ink-60 leading-relaxed">
            Для надёжной A/B-статистики на одном продукте берите от <b>30 000 ₽</b>.
            Большинство брендов в вашей категории стартуют со <b>150 000 ₽</b> — этого хватает на охват, A/B и персонального менеджера.
          </div>
        </div>
        <button onClick={() => setTipOpen(!tipOpen)}
          className="inline-flex items-center h-8 px-3 rounded-pill border-2 border-ink-12 text-[12.5px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40 flex-shrink-0">
          {tipOpen ? "Скрыть формулу" : "Как считается?"}
        </button>
      </Card>

      {tipOpen && (
        <Card className="!p-5 mb-4 fade-up">
          <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40 mb-2">Методика прогноза</div>
          <pre className="font-mono text-[12px] bg-ink-6 p-3 rounded-sm leading-relaxed mb-2.5 whitespace-pre-wrap">{`reach ≈ Σ(охват канала × фактор соцсети)
CPV = budget / reach   ·   CPM = budget / показы × 1000`}</pre>
          <div className="text-[13px] text-ink-60 leading-relaxed">
            Берём медиану CPV последних 20 размещений каждого канала, частоту 1.35, средний CTR 1.7% и конверсию 4.2% от кликов — по вашей категории «<b>{cat?.label ?? "—"}</b>».
          </div>
        </Card>
      )}

      <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40 mb-2.5">Популярные суммы</div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        {(cfg?.presets ?? []).map((p) => {
          const active = draft.budget === p.value;
          return (
            <button key={p.value} onClick={() => setBudget(p.value)}
              className={cn("relative text-left p-4 rounded-md border-2 transition-colors",
                active ? "border-ink bg-cream" : "border-ink-12 hover:border-ink/40 bg-white")}>
              {p.flag && (
                <span className={cn("absolute top-2 right-2 px-2 py-0.5 rounded-pill text-[10px] font-display font-bold uppercase tracking-wider",
                  p.flag_color === "pink" ? "bg-pink text-[var(--fg-on-accent)]" : "bg-lime text-[var(--fg-on-accent)]")}>
                  {p.flag}
                </span>
              )}
              {active && <CheckCircle2 size={16} className="absolute top-2 left-2 text-ink" />}
              <div className="font-display font-extrabold text-[18px] mb-1">{p.label}</div>
              <div className="text-[12px] text-ink-40">{p.meta}</div>
            </button>
          );
        })}
      </div>

      <Card className="!p-7 mb-4">
        <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40 mb-4">Точная сумма</div>
        <Slider min={cfg?.min ?? 3000} max={cfg?.max ?? 500000} step={cfg?.step ?? 1000}
          value={draft.budget || 70000} onChange={setBudget} fmt={fmtRubFull} />
        <div className="flex justify-between mt-3 text-ink-40 text-[11px] font-mono">
          <span>3К</span><span>70К</span><span>150К</span><span>300К</span><span>500К</span>
        </div>
      </Card>

      <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40 mb-2.5">Прогноз медиаплана</div>
      {fc && cfg && (
        <div className="grid grid-cols-4 gap-3 mb-4">
          <Metric label="Каналов"   tip={cfg.metric_tips.bloggers}    val={`~${fc.bloggers}`}             sub="в подборке" />
          <Metric label="Охват"     tip={cfg.metric_tips.reach}        val={`~${fmtNum(fc.reach)}`}        sub="уник. пользователей" />
          <Metric label="Показы"    tip={cfg.metric_tips.impressions} val={`~${fmtNum(fc.impressions)}`}  sub={`частота ${fc.frequency}`} />
          <Metric label="CPV"       tip={cfg.metric_tips.cpv}          val={`${fc.cpv} ₽`}                 sub="за просмотр" accent tipAlign="right" />
          <Metric label="CPM"       tip={cfg.metric_tips.cpm}          val={`${fc.cpm} ₽`}                 sub="за 1000 показов" />
          <Metric label="Клики"     tip={cfg.metric_tips.clicks}       val={`~${fmtNum(fc.clicks)}`}       sub="CTR 1,7%" />
          <Metric label="CPC"       tip={cfg.metric_tips.cpc}          val={`${fc.cpc} ₽`}                 sub="за клик" />
          <Metric label="Лиды"      tip={cfg.metric_tips.leads}        val={`~${fc.leads}`}                sub={`CPL ${fmtRub(fc.cpl)}`} tipAlign="right" />
        </div>
      )}

      {cfg && (
        <div className="grid grid-cols-2 gap-4">
          <Tiers budget={draft.budget || 70000} tiers={cfg.tiers} />
          <Benchmarks data={cfg.benchmarks} />
        </div>
      )}
    </>
  );
}

function Metric({ label, val, sub, accent, tip, tipAlign }: {
  label: string; val: string; sub: string; accent?: boolean; tip?: string; tipAlign?: "left" | "center" | "right";
}) {
  return (
    <div className={cn("p-4 rounded-md border", accent ? "bg-ink text-white border-ink" : "bg-white border-ink-12")}>
      <div className={cn("flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-display font-semibold",
        accent ? "text-white/70" : "text-ink-40")}>
        {label}
        {tip && <InfoTip align={tipAlign ?? "center"}>{tip}</InfoTip>}
      </div>
      <div className={cn("font-display font-extrabold text-[26px] tabular-nums mt-1 leading-none",
        accent && "text-lime")}>
        {val}
      </div>
      <div className={cn("text-[11.5px] mt-1", accent ? "text-white/55" : "text-ink-40")}>{sub}</div>
    </div>
  );
}

function Tiers({ budget, tiers }: { budget: number; tiers: { from: number; title: string; highlight: boolean; perks: string[] }[] }) {
  return (
    <Card className="!p-5.5">
      <div className="flex items-center gap-2 mb-1.5">
        <TrendingUp size={16} className="text-purple-900" />
        <div className="font-display font-bold text-[16px]">Что даёт больший бюджет</div>
      </div>
      <div className="text-[12.5px] text-ink-40 mb-3 leading-relaxed">Чем выше бюджет — тем точнее подбор и больше поддержки от нас.</div>
      <div className="grid gap-3">
        {tiers.map((t, idx) => {
          const reached = budget >= t.from;
          const nextFrom = tiers[idx + 1]?.from;
          const current = reached && (nextFrom ? budget < nextFrom : true);
          return (
            <div key={t.from} className={cn("p-3 rounded-md border transition-opacity",
              current ? "border-lime bg-lime/10" : reached ? "border-ink-12 bg-white" : "border-ink-12 bg-ink-6 opacity-60")}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-[12px] font-bold text-ink-40">
                  {t.from <= 30000 ? "от 30 000 ₽" : `от ${fmtRubFull(t.from)}`}
                </span>
                <span className="font-display font-bold text-[14px]">{t.title}</span>
                {current && <span className="ml-1 px-2 py-0.5 rounded-pill bg-lime text-[10px] font-display font-bold uppercase">ваш уровень</span>}
              </div>
              <div className="grid gap-1.5">
                {t.perks.map((perk, i) => (
                  <div key={i} className="flex items-start gap-2 text-[12.5px] text-ink-60 leading-snug">
                    {reached ? <CheckCircle2 size={14} className="text-lime flex-shrink-0 mt-0.5" /> : <Lock size={12} className="text-ink-40 flex-shrink-0 mt-1" />}
                    {perk}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function Benchmarks({ data }: { data: { axis_max: number; rows: { id: string; label: string; cpv: number; kind: string; note: string }[]; reco: string } }) {
  return (
    <Card className="!p-5.5">
      <div className="flex items-center gap-2 mb-1.5">
        <BarChart size={16} className="text-ink-60" />
        <div className="font-display font-bold text-[16px]">Бенчмарки рынка</div>
        <InfoTip align="right">CPV — стоимость одного просмотра. Чем ниже столбец, тем дешевле охват. Сравниваем наш прогноз со средним по рынку и категории.</InfoTip>
      </div>
      <div className="text-[12.5px] text-ink-40 mb-4 leading-relaxed">Рекомендуемый CPV для вашего продукта — по чему ориентироваться.</div>
      <div className="grid gap-3.5">
        {data.rows.map((r) => {
          const w = Math.min(100, (r.cpv / data.axis_max) * 100);
          const color = r.kind === "best" ? "var(--wb-lime)" : r.kind === "mkt" ? "var(--wb-pink)" : "var(--wb-black)";
          return (
            <div key={r.id}>
              <div className="flex justify-between items-baseline mb-1.5 gap-2.5">
                <span className="text-[12.5px] font-semibold truncate">{r.label}</span>
                <span className="font-mono font-bold text-[13px] flex-shrink-0">{r.cpv} ₽</span>
              </div>
              <div className="h-2 rounded-pill bg-ink-6 overflow-hidden">
                <div className="h-full rounded-pill transition-[width] duration-300" style={{ width: `${w}%`, background: color }} />
              </div>
              <div className="text-[11px] text-ink-40 mt-1">{r.note}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 p-3.5 rounded-md bg-cream text-[12.5px] text-ink-60 leading-relaxed">
        <b>Вывод. </b>{data.reco}
      </div>
    </Card>
  );
}
